import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  styled,
  Chip,
  Avatar,
  Button,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  alpha,
  ListItemIcon,
  useTheme,
  Box,
  Tooltip,
} from '@mui/material';
import { getInitialsFromLongName } from '@askporter/utils';
import { Icon, IconSize } from '../../../Icon';
import { RevampedIcon, RevampedIconSize } from '../../../RevampedIcon';
import type { BaseAutocompleteProps, BaseAutocompleteValue } from '../../types';
import { CHIP_MIN_WIDTH } from '../utils/chipLayoutConstants';
import { highlightMatch } from '../utils/createRenderOption';

const StyledChip = styled(Chip)(({ theme }) => ({
  '&.MuiChip-root': {
    borderRadius: '6px',
    border: `1px solid ${theme.custom.colours?.grey300}`,
    backgroundColor: '#FFF',
    padding: '2px 4px 2px 5px',
    minWidth: `${CHIP_MIN_WIDTH}px`,
    maxWidth: '100%', // Fill container, let container control max size
    boxSizing: 'border-box', // Ensure border is included in width
  },
  '&.Mui-disabled': {
    backgroundColor: '#f2f4f9',
    opacity: 1,
    cursor: 'not-allowed',
    '& .MuiChip-label': {
      color: '#667085',
    },
  },
  '& .MuiAvatar-root': {
    width: '16px',
    height: '16px',
    marginRight: '5px',
  },
  '& .MuiChip-label': {
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '20px',
    color: theme.custom.colours?.grey700,
    marginRight: '5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiChip-deleteIcon': {
    color: theme.custom.colours?.grey700,
    flexShrink: 0,
  },
}));

export const AutocompleteChip = ({
  index,
  value,
  itemProps,
}: {
  index: number;
  value: BaseAutocompleteValue;
  itemProps: Record<string, unknown>;
}) => {
  return (
    <Tooltip title={value.label} placement="top">
      <StyledChip
        key={index}
        variant="outlined"
        label={value.label}
        size="small"
        deleteIcon={<CloseIcon />}
        avatar={value.avatar ? <Avatar src={value.avatar} /> : undefined}
        {...itemProps}
      />
    </Tooltip>
  );
};

const StyledText = styled('div')(({ theme }) => ({
  backgroundColor: theme.custom.colours?.grey100,
  borderRadius: '3px',
  padding: '0 5px',
  margin: '0 3px 0 5px',
}));

export const LimitCountChip = ({
  numberOfHiddenValues,
  disabled,
  onClick,
  t,
}: {
  numberOfHiddenValues: number;
  disabled?: boolean;
  onClick: () => void;
  t: (key: string, options: Record<string, string | number>) => string;
}) => {
  return (
    <StyledChip
      disabled={disabled}
      onClick={onClick}
      aria-label={t('common:autocomplete:chip_count_button', { count: numberOfHiddenValues })}
      label={
        <>
          + <StyledText>{numberOfHiddenValues}</StyledText>
        </>
      }
      size="small"
      sx={{
        '&.MuiChip-root': {
          paddingRight: 0,
          minWidth: 'auto',
        },
        '& .MuiChip-label': {
          display: 'flex',
          flexDirection: 'row',
        },
      }}
    />
  );
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'hasBottomBorder',
})<{ hasBottomBorder: boolean }>(({ theme, hasBottomBorder }) => ({
  justifyContent: 'flex-start',
  padding: '10px 10px 10px 8px',
  marginBottom: '4px',
  borderRadius: 0,
  borderBottom: hasBottomBorder ? `1px solid ${theme.custom.colours?.grey300}` : 'none',
  '& .MuiButton-icon': {
    marginLeft: 0,
  },
  fontSize: '16px',
  fontWeight: '500',
  lineHeight: '24px',
  color: theme.custom.colours?.grey900,
}));

export const OptionsListAddAction = ({
  onClick,
  label,
  hasBottomBorder = true,
}: {
  onClick: () => void;
  label: string;
  hasBottomBorder?: boolean;
}) => {
  const theme = useTheme();
  return (
    <StyledButton
      fullWidth
      onClick={onClick}
      startIcon={
        <RevampedIcon
          fullPath="/media/icons/design-system/plus/20px.svg"
          iconSize={RevampedIconSize.SM}
          firstPathStroke={theme.palette.primary.main}
        />
      }
      hasBottomBorder={hasBottomBorder}
    >
      {label}
    </StyledButton>
  );
};

const StyledListItem = styled(ListItemButton)<{ component: 'li' }>(({ theme, selected }) => ({
  margin: '2px 6px',
  borderRadius: '6px',
  padding: '10px 12px 10px 8px',
  whiteSpace: 'nowrap',
  minWidth: 'fit-content',
  '& .MuiListItemAvatar-root': {
    // We need to override the minWidth of the ListItemAvatar to 24px
    minWidth: '24px',
    marginRight: '8px',
    '& .MuiAvatar-root': {
      width: '24px',
      height: '24px',
    },
  },
  '& .MuiListItemText-root': {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1 1 auto',
    minWidth: 0,
    marginRight: '12px',
    '& .MuiListItemText-primary': {
      color: selected ? alpha(theme.palette.primary.main, 0.8) : theme.custom.colours?.grey900,
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '24px',
      whiteSpace: 'nowrap',
    },
    '& .MuiListItemText-secondary': {
      color: selected ? alpha(theme.palette.primary.main, 0.7) : theme.custom.colours?.grey600,
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      whiteSpace: 'nowrap',
    },
  },
  '& .MuiListItemIcon-root': {
    paddingLeft: '8px',
    // We need to override the minWidth of the ListItemIcon to 20px
    minWidth: '24px',
    flexShrink: 0,
    marginLeft: 'auto',
  },
}));

export const AutocompleteListItem = ({
  option,
  optionProps,
  isSelected,
  isAvatarVisible = true,
  highlightSearchText = false,
  searchQuery = '',
}: {
  option: ReturnType<BaseAutocompleteProps['mapOptionToValue']>;
  optionProps: Record<string, unknown>;
  isSelected: boolean;
  isAvatarVisible?: boolean;
  highlightSearchText?: boolean;
  searchQuery?: string;
}) => {
  const theme = useTheme();

  // Apply highlighting if enabled and search query exists
  const displayLabel = highlightSearchText && searchQuery ? highlightMatch(option.label, searchQuery) : option.label;

  return (
    <StyledListItem {...optionProps} selected={isSelected} component="li">
      {isAvatarVisible && (
        <ListItemAvatar>
          <Avatar sx={{ fontSize: '10px', color: '#667085' }} src={option.avatar}>
            {getInitialsFromLongName(option.label)}
          </Avatar>
        </ListItemAvatar>
      )}
      <ListItemText primary={displayLabel} secondary={option.subLabel} />
      <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
        {option.iconPath && <Icon size={IconSize.MD} fullPath={option.iconPath} />}
        {isSelected && (
          <Box sx={{ pl: option.iconPath ? 1 : 0 }}>
            <RevampedIcon
              fullPath="/media/icons/design-system/tick/20px.svg"
              iconSize={RevampedIconSize.SM}
              firstPathStroke={theme.palette.primary.main}
            />
          </Box>
        )}
      </ListItemIcon>
    </StyledListItem>
  );
};
